import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CommunityTableRequestData,
  CommunityUsersFilters,
  CommunityUsersFiltersFunc,
  CommunityUsersParams,
  TCommunityUser,
  TCommunityUsersList
} from './types'
import axios, { CancelTokenSource } from 'axios'
import { useDebounce } from 'hooks/hooks'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import './style.scss'
import { OptionsMenu, columns } from './constants'
import { Table } from '../../../../components/Table'
import {
  fullFormatPhoneNumber,
  isTableHasParams
} from '../../../../helper/common'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../../redux/store/user/getters'
import { ROLES } from 'features/Permission'
import { routes } from '../../../../router'
import useRouter from '../../../../hooks/useRouter'
import { Button, BUTTON_TYPES } from '../../../../components/Button'

import { USER_STATUSES } from '../../../../constants'
import { UserArchivePopup } from '../../../../pages/CRUDUser/UserArchivePopup'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { ColumnsType } from 'antd/es/table'
import { setLoading } from '../../../../redux/store/common/slice'
import { onStartChat } from '../../utils'
import { getCommunityUsers } from './api'
import { ReactComponent as Plus16 } from '../../../../assets/svg/Plus16.svg'
import { UserRolesFilterComponent } from '../../../../components/UserRolesFilterComponent/UserRolesFilterComponent'
import {
  Typography,
  TYPOGRAPHY_WEIGHT
} from '../../../../components/Typography'
import { Input } from '../../../../components/Input'
import { ReactComponent as Close16 } from '../../../../assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from '../../../../assets/svg/Search16.svg'
import { filterColumns } from './utils'

export const CommunityUsers = () => {
  const user = useSelector(getUser)
  const { push } = useRouter()
  const dispatch = useDispatch()
  const [searchValue, setSearchValue] = useState('')
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [tableData, setTableData] = useState<TCommunityUser[]>([])
  const [tableColumns, setTableColumns] = useState<ColumnsType<TCommunityUser>>(
    []
  )
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [selectedByActionsUser, setSelectedByActionsUser] =
    useState<TCommunityUsersList | null>(null)

  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [selectedRows, setSelectedRows] = useState<TCommunityUsersList[]>([])
  const [sortParams, setSortParams] = useState<CommunityTableRequestData>()
  const [filters, setFilters] = useState<CommunityUsersFilters>({})
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()

  const debouncedSearch = useDebounce(searchValue, 500)
  const canEditUser = user.role === ROLES.COMMUNITY_PRESIDENT

  useEffect(() => {
    return () => {
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const onChangeRole = (tab?: string) => {
    setFilters({})
    setSortParams({})
    setSearchValue('')
    setActiveTab(tab ?? null)
  }

  const createUser = () => {
    push(`${routes.createUserProfile}/new`)
  }
  const handleRedirect = () => {
    push(`${routes.createUserProfile}/${selectedRowKeys[0]}`)
  }

  const onArchive = useCallback((user) => {
    setIsArchiveOpen(true)
    setSelectedByActionsUser(user)
  }, [])

  const applyFilters: CommunityUsersFiltersFunc = (field, appliedFilters) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [field]: appliedFilters }
      getTableData({ ...sortParams, filters: newFilters })
      return newFilters
    })
  }

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: CommunityTableRequestData) => {
    dispatch(setLoading(true))
    if (tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: CommunityUsersParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ?? pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getCommunityUsers(activeTab, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        setTableColumns(
          filterColumns(
            activeTab,
            columns({
              applyFilters,
              onStartChat,
              canEditUser,
              selectedRole: activeTab,
              filters,
              community: user.community || '',
              onArchive
            })
          )
        )
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            name: i.first_name,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams, ...filters }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab])

  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  return (
    <>
      <div className="community-users column">
        <div className="community-users__header row align-center space-between">
          <div className="row gap-8">
            <UserRolesFilterComponent
              roles={OptionsMenu}
              onChange={onChangeRole}
            />
            <div className="community-users__actions row align-center">
              {selectedRowKeys.length ? (
                <Typography.Body1
                  className="table-wrapper__select-title"
                  weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
                >
                  Selected {selectedRowKeys.length} of {totalNumber}
                </Typography.Body1>
              ) : (
                <Input
                  className="allow-clear community-users-search"
                  propsInput={{
                    allowClear: { clearIcon: <Close16 /> },
                    placeholder: PLACEHOLDERS.SEARCH_RFP_USERS,
                    prefix: <Search16 />,
                    value: searchValue,
                    onChange: (e) => setSearchValue(e.target.value)
                  }}
                />
              )}
            </div>
          </div>
          <div className="row space-between gap-8">
            {canEditUser && !selectedRowKeys.length && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={createUser}
                upperCase
              >
                {BTN_TXT.CREATE_USER}
              </Button>
            )}
            {selectedRowKeys.length === 1 && canEditUser && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                onClick={handleRedirect}
                icon={<Edit16 />}
                upperCase
                className="table-wrapper__header_buttons__edit-btn"
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {((!!selectedRows.length &&
              selectedRows.filter((i) => i.status !== USER_STATUSES.ARCHIVED)
                .length === selectedRows.length) ||
              isArchiveOpen) && (
              <UserArchivePopup<TCommunityUsersList>
                selected={
                  isArchiveOpen && selectedByActionsUser
                    ? [selectedByActionsUser]
                    : selectedRows
                }
                callback={() => {
                  setIsArchiveOpen(false)
                  setSelectedByActionsUser(null)
                  setSelectedRowKeys([])
                  setSelectedRows([])
                  getTableData({ ...sortParams, ...filters })
                }}
                isTable
                isOpenFromTheOutside={isArchiveOpen}
                setIsOpenFromTheOutside={setIsArchiveOpen}
              />
            )}
          </div>
        </div>
        <Table<TCommunityUser>
          dataSource={tableColumns.length ? tableData : []}
          columns={tableColumns}
          emptyText="No users found"
          className="community-users-table mt-8"
          scroll={{ scrollToFirstRowOnChange: true, x: 'max-content' }}
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          hasSearchOrFilters={isTableHasParams<CommunityUsersFilters>(
            debouncedSearch,
            filters
          )}
          createButton={
            canEditUser
              ? {
                  buttonText: BTN_TXT.CREATE_USER,
                  availability: true,
                  action: createUser
                }
              : undefined
          }
          rowSelection={
            canEditUser
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled: record.uuid === user.uuid
                  })
                }
              : undefined
          }
        />
      </div>
    </>
  )
}
